import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import ContractorUpdate from "./ContractorUpdate";
import { useContractorData } from "../hooks";
import { memo } from "react";
import TaxStatus from "@/components/TaxStatus";
import {
  Module,
  PATH_USER,
  PATH_TIMESHEET_ENTRIES_CONTRACTOR_VISITS,
} from "@/constants";
import { DetailsButton } from "@/components/DetailsButton";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { useFormatters } from "@/lib/hooks/useFormatters";

const ContractorDetails = memo(() => {
  const { t } = useTranslation();
  const contractor = useContractorData();
  const usersPermissions = useModulePermissions(Module.Users);
  const { formatDateTime, formatNumber } = useFormatters();

  if (!contractor) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("contractors.details.title")}</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <div className="space-y-1">
            <dt className="text-sm font-medium text-gray-500">
              {t("contractors.fields.user_ref_number")}
            </dt>
            <dd className="text-sm text-gray-900">
              {contractor.user_ref_number}
            </dd>
          </div>
          <div className="space-y-1">
            <dt className="text-sm font-medium text-gray-500">
              {t("contractors.fields.personal_id_number")}
            </dt>
            <dd className="text-sm text-gray-900">
              {contractor.personal_id_number}
            </dd>
          </div>
          <div className="space-y-1">
            <dt className="text-sm font-medium text-gray-500">
              {t("contractors.fields.birth_date")}
            </dt>
            <dd className="text-sm text-gray-900">{contractor.birth_date}</dd>
          </div>
          <div className="space-y-1">
            <dt className="text-sm font-medium text-gray-500">
              {t("contractors.fields.gender")}
            </dt>
            <dd className="text-sm text-gray-900">
              {contractor.gender ? t(`gender.${contractor.gender}`) : ""}
            </dd>
          </div>
          <div className="space-y-1">
            <dt className="text-sm font-medium text-gray-500">
              {t("contractors.fields.registered_by_name")}
            </dt>
            <dd className="text-sm text-gray-900">
              {contractor.registered_by_name}
            </dd>
          </div>
          <div className="space-y-1">
            <dt className="text-sm font-medium text-gray-500">
              {t("contractors.fields.tax_status")}
            </dt>
            <dd className="text-sm text-gray-900">
              <TaxStatus value={contractor.tax_status} className="w-full" />
            </dd>
          </div>
          <div className="space-y-1">
            <dt className="text-sm font-medium text-gray-500">
              {t("contractors.fields.planned_hours_approved_at")}
            </dt>
            <dd className="text-sm text-gray-900">
              {formatDateTime(contractor.planned_hours_approved_at)}
            </dd>
          </div>
          <div className="space-y-1">
            <dt className="text-sm font-medium text-gray-500">
              {t("contractors.fields.planned_hours_approved_value")}
            </dt>
            <dd className="text-sm text-gray-900">
              {formatNumber(contractor.planned_hours_approved_value)}
            </dd>
          </div>
          <div className="space-y-1">
            <dt className="text-sm font-medium text-gray-500">
              {t("contractors.fields.is_active")}
            </dt>
            <dd className="text-sm text-gray-900">
              <Badge
                variant={contractor.is_active ? "secondary" : "destructive"}
              >
                {contractor.is_active
                  ? t("contractors.status.active")
                  : t("contractors.status.inactive")}
              </Badge>
            </dd>
          </div>
          {contractor.user_id && usersPermissions.can_read && (
            <div className="space-y-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("contractors.details.userCard")}
              </dt>
              <dd className="text-sm text-gray-900">
                <DetailsButton
                  path={PATH_USER}
                  pathParams={{ userId: contractor.user_id }}
                  label="contractors.details.userDetailsButton"
                />
              </dd>
            </div>
          )}
          <div className="space-y-1">
            <dt className="text-sm font-medium text-gray-500">
              {t("contractors.details.timesheet")}
            </dt>
            <dd className="text-sm text-gray-900">
              <DetailsButton
                path={PATH_TIMESHEET_ENTRIES_CONTRACTOR_VISITS}
                pathParams={{ userId: contractor.id, shiftReportId: null }}
                label="contractors.details.timesheetLink"
              />
            </dd>
          </div>
        </dl>

        <Separator className="my-6" />

        <ContractorUpdate />
      </CardContent>
    </Card>
  );
});

export default ContractorDetails;
